import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import BillingInvoiceRecipientFormSection
	from "modules/project/components/forms/formSections/InvoiceRecipientFormSection";
import type React from "react";
import { useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import TimingFormSection from "~/components/formSections/TimingFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useOrderTitleValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useOrderTitleValidationComparisonData.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { createOrder } from "~/modules/project/api/order/orderApiDispatchers.ts";
import type { CreateOrderData } from "~/modules/project/api/order/orderTypes.ts";
import OrderBudgetFormSection from "~/modules/project/components/forms/formSections/OrderBudgetFormSection";
import OrderStatusFormSection from "~/modules/project/components/forms/formSections/OrderStatusFormSection";
import OrderTitleAndNumberFormSection
	from "~/modules/project/components/forms/formSections/OrderTitleAndNumberFormSection";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { convertLocalToUTCDate, formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type CreateOrderFormProps = {
	clientId: string;
	onSuccess: () => void;
	onCancel: () => void;
	projectBillingSettings:BillingSettings;
	projectId: string;
	projectPhaseId: string;
};

interface CreateOrderFormData
	extends Omit<
		CreateOrderData,
		| "billingModeId"
		| "quotaId"
		| "projectId"
		| "projectPhaseId"
		| "confirmedAt"
		| "startDate"
		| "endDate"
		| "paymentTargetDays"
		| "manDays"
	> {
	clientContactPersonId: string | null;
	confirmed: boolean;
	invoiceRecipientId: string | null;
	manDays: number;
	endDate: Date | null;
	startDate: Date | null;
}

const CreateOrderForm: React.FC<CreateOrderFormProps> = ({
	onSuccess,
	onCancel,
	clientId,
	projectBillingSettings,
	projectId,
	projectPhaseId,
}) => {
	const [busy, setBusy] = useState(false);
	const titleComparisonData = useOrderTitleValidationComparisonData({ projectId, projectPhaseId });
	const schema = useMemo(() => {
		return yup
			.object({
				budgetCents: yup.number().required(),
				confirmed: yup.boolean().required(),
				clientContactPersonId: yup.string().nullable().default(null),
				endDate: yup
					.date()
					.required()
					.min(yup.ref("startDate"), "Das Startdatum muss vor dem Enddatum liegen."),
				invoiceOrderNumberRequired: yup.boolean().required(),
				invoiceRecipientId: yup.string().nullable().default(null),
				manDays: yup.number().typeError(t("form:validationErrors.number.requiredTypeError")).required(),
				orderNumber: yup.string().default(null),
				startDate: yup.date().required(),
				title: yup.string().required().unique(titleComparisonData),
			})
			.test("atLeastOneField", "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.", (value) =>
				Boolean(value.clientContactPersonId || value.invoiceRecipientId),
			);
	}, [titleComparisonData]);

	const defaultValues = useMemo(() => {
		return {
			budgetCents: 0,
			clientContactPersonId: projectBillingSettings.clientContactPersonId || "",
			confirmed: false,
			endDate: null,
			invoiceOrderNumberRequired: true,
			invoiceRecipientId: projectBillingSettings.invoiceRecipientId || "",
			manDays: 0,
			orderNumber: "",
			startDate: null,
			title: "",
		};
	}, []);
	const {
		handleSubmit,
		control,
		formState: { errors, isSubmitted, isValid, isDirty },
		watch,
		setValue,
		trigger,
	} = useForm<CreateOrderFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateOrderFormData>(schema),
	});
	const formIsSubmittable = useFormIsSubmittable({ isDirty, isValid, isSubmitted, isLoading: busy });

	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(clientId);
	const clientsInvoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(clientId);

	const onSubmit: SubmitHandler<CreateOrderFormData> = async (data: CreateOrderFormData) => {
		setBusy(true);
		const orderData = {
			...data,
			startDate: formatDateToYYYYMMDD(data.startDate!),
			endDate: formatDateToYYYYMMDD(data.endDate!),
			projectId: projectId,
			projectPhaseId: projectPhaseId,
			confirmedAt: data.confirmed ? convertLocalToUTCDate(new Date()) : null,
		};

		try {
			await createOrder({ projectId, orderData });
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neue Bestellung</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}

				<FormSectionGroup>
					<OrderTitleAndNumberFormSection control={control} />
					<OrderStatusFormSection control={control} />
					<OrderBudgetFormSection control={control}
											watch={watch} />
					<TimingFormSection control={control}
									   watch={watch}
									   setValue={setValue}
									   errors={errors}
									   title={"Laufzeit"} />

					<FormSection title="Rechnungsempfänger">
						<BillingInvoiceRecipientFormSection
							control={control}
							clientContactPersonSelectOptions={clientContactPersonSelectOptions}
							// @ts-expect-error - needs to be fixed
							errorMessage={errors[""]?.message}
							invoiceRecipientSelectOptions={clientsInvoiceRecipientSelectOptions}
							setValue={setValue}
							trigger={trigger}
							watch={watch}
						/>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint
					show={isSubmitted && !isValid}
					className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateOrderForm;
